import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  login as apiLogin,
  getToken,
  removeToken,
  decodeToken,
} from '../services/api';

interface User {
  id: string;
  username: string;
  role: 'admin' | 'user';
  name: string;
  status: 'active' | 'inactive';
  products?: string[];
  allowedSources?: string[];
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock users for demonstration
const mockUsers: User[] = [
  {
    id: '1',
    username: 'admin',
    role: 'admin',
    name: 'System Administrator',
    status: 'active',
  },
  {
    id: '2',
    username: 'trader1',
    role: 'user',
    name: 'Ahmed Mohammed',
    status: 'active',
    products: ['apples', 'oranges', 'bananas'],
    allowedSources: ['central-market', 'wholesale-hub'],
  },
  {
    id: '3',
    username: 'trader2',
    role: 'user',
    name: 'Fatima Al-Zahra',
    status: 'inactive',
    products: ['tomatoes', 'onions', 'potatoes'],
    allowedSources: ['farm-direct', 'local-market'],
  },
];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored JWT token
    const token = getToken();
    if (token) {
      try {
        const userInfo = decodeToken(token);
        if (userInfo && userInfo.exp > Date.now() / 1000) {
          // Token is valid
          setUser({
            id: userInfo.sub || userInfo.user_id,
            username: userInfo.username || userInfo.sub,
            role: userInfo.role || 'user',
            name: userInfo.name || userInfo.username || 'User',
            status: 'active',
          });
        } else {
          // Token expired
          removeToken();
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        removeToken();
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    setIsLoading(true);

    try {
      const { token, user: userInfo } = await apiLogin(username, password);

      // Create user object from token
      const user: User = {
        id: userInfo.sub || userInfo.user_id,
        username: userInfo.username || userInfo.sub,
        role: userInfo.role || 'user',
        name: userInfo.name || userInfo.username || 'User',
        status: 'active',
      };

      setUser(user);
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    removeToken();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
